/* @flow */
import { Observable, Scheduler } from 'rxjs';
import { combineEpics } from 'redux-observable';
import { fromJS } from 'immutable';
import { prop } from 'ramda';
import createDebugger from 'debug';
const debug = createDebugger('app:modules:insights:epics'); // eslint-disable-line no-unused-vars

import { setViewOptions } from './actions.js';
import {
  FETCH_DASHBOARD,
  SELECT_ENCLAVE,
  PAGINATE,
  PAGINATE_SUCCESS,
  PAGINATE_FAILURE,
  APPLY_OPTIONS,
  FETCH_TIME_RANGE,
  FETCH_TIME_RANGE_SUCCESS,
  FETCH_TIME_RANGE_FAILURE,
  SAVE_DATE_RANGE,
  REGISTER_ACTIVITY,
  SET_DEFAULT_ENCLAVE,
  SAVE_DEFAULT_ENCLAVE,
} from './action-types.js';
import { FETCH_UPDATED_TIME_RANGE as FETCH_METRICS } from 'modules/metrics/action-types.js';
import { FETCH_SUCCESS as FETCH_ENCLAVES_SUCCESS } from 'modules/enclave/action-types.js';
import { FETCH_SUCCESS as FETCH_PROFILE_SUCCESS } from 'modules/profile/action-types.js';

import { enclaveSelection, savedRanges } from 'modules/insights/getters.js';
import { currentTimeFilter } from './getters.js';
import { error } from 'modules/alerts/actions.js';
import { computeDateRangeDaySelection } from 'trustar/utils/dashboard';

/* GET requests w/ query params:
 *
 * ?enclave= (default: first enclave)
 * ?from= (default: 7)
 * ?to= (default: 0)
 * ?pageSize= (default: 10)
 * ?startPage= (default: 0)
 *
**/

const insightCategories = [
  'enclaveCorrelations',
  'communityCorrelations',
  'enclaveIoCs',
];

const trendCategories = [
  'communityTrendingIocs',
  'malware',
  'cves',
];

const categoryToEndpoint = fromJS({
  enclaveCorrelations: 'enclave-correlations',
  communityCorrelations: 'community-correlations',
  enclaveIoCs: 'iocs-correlations',
  communityTrendingIocs: 'community-trending-iocs',
  malware: 'malware-indicators',
  cves: 'cve-indicators',
});

const defaultRange = 3;

export const generateDefaultRange = () => {

  const fromDate = new Date();
  fromDate.setDate(fromDate.getDate() - defaultRange);

  return {
    from: fromDate.getTime(),
    to: new Date().getTime(),
  };
};

const createEndpoint = (category: string, parameters: Object = {}) => {

  const defaultDateRange = generateDefaultRange();

  const {
    enclave = '',
    from = defaultDateRange.from,
    to = defaultDateRange.to,
    startPage = 0,
    pageSize = 10,
    refresh = false,
  } = parameters;

  const baseURL = `/services/insights/${categoryToEndpoint.get(category)}`;
  const params = `enclave=${enclave}&from=${from}&to=${to}&pageSize=${pageSize}&startPage=${startPage}&refresh=${refresh.toString()}`;

  return `${baseURL}?${params}`;
};

const fromJSON = (response: Object) => {
  return fromJS(response)
    .update('data', d => d.get('items'))
    .update('status', (s = null) => s)
    .update('totalElements', e => e > 50 ? 50 : e);
};

/**
 * Map over all the categories and fetch each
 *
 * NOTE: Whenever a report is removed we also fetch. This is how stale reports
 * (i.e. reports that no longer exist) get cleared out of insights data.
 *
 * NOTE: We made the explore view the default which means the dashboard will not
 * be shown on inital load (unless they deep link). Since the dashboard
 * currently takes a long time to load we're prefetching the data here.
 * However, since app initialization is a very busy time I want to deprioritize
 * this fetch. The 2s is arbitrary but should escape the heaviest thread usaeg
 * on initialization.
 *
 * requestIdleCallback is not supported in safari or Edge (let alone IE)
 * as of this writing. It also doesn't come build into Rx. So for now let's just
 * schedule on animation frame
 */
const initEpic = (action$, store, { ajax }) =>
  Observable.merge(
    action$.ofType(FETCH_DASHBOARD) // See NOTE
  )
    .mergeMap(({ payload }) => {
      const savedDateRanges = savedRanges(store.getState());
      const insights = savedDateRanges.get('insights');
      const trends = savedDateRanges.get('trends');
      return Observable.from([
        ...insightCategories.map(category => ({
          type: FETCH_TIME_RANGE,
          payload: { category, refresh: !!payload , ...insights.toJS() }
        })),
        ...trendCategories.map(category => ({
          type: FETCH_TIME_RANGE,
          payload: { category, refresh: !!payload , ...trends.toJS() }
        })),
      ]);
    });

/* Select a new enclave and re-fetch all categories */
const changeEnclaveEpic = (action$, store, { ajax }) =>
  action$.ofType(SELECT_ENCLAVE)
    .mergeMap(({ payload: { id } }) => {
      return Observable.from(insightCategories.map(category => ({
        type: FETCH_TIME_RANGE,
        payload: { category, enclave: id, ...generateDefaultRange() },
      })));
    });

/* Sets new date range options, fetches based on selection */
const applyOptionsEpic = (action$, store, { ajax }) =>
  action$.ofType(APPLY_OPTIONS)
    .mergeMap(() => {
      const enclave = enclaveSelection(store.getState()).get('enclaveId');
      const dateOptions = currentTimeFilter(store.getState());
      const category = dateOptions.get('category');
      const daysBack = dateOptions.get('daysBack');

      const range = daysBack
        ? computeDateRangeDaySelection(daysBack)
        : {
          from: dateOptions.get('dateSince'),
          to: dateOptions.get('dateUntil'),
        };

      let endpoints;

      switch(category) {
      case 'insights':
        endpoints = insightCategories;
        break;
      case 'trends':
        endpoints = trendCategories;
        break;
      default:
        return Observable.empty();
      }

      const actions = endpoints.map(category => ({
        type: FETCH_TIME_RANGE,
        payload: { category, enclave, ...range }
      }));

      if (category === 'insights') {
        actions.push({
          type: FETCH_METRICS,
          payload: { ...range }
        });
      }

      actions.push({
        type: SAVE_DATE_RANGE,
        payload: { category, ...range },
      });

      return Observable.from(actions);
    })
    .catch(err => {
      const { status } = err;
      // TODO_LOG
      //if (status !== 401 && status !== 403 && status !== 502) {
      //  Raven.captureException(
      //    new Error('An Error occurred in insights applyOptionsEpic.'),
      //    {extra: { error: err }}
      //  );
      //}
      return Observable.from(categoryToEndpoint.keySeq().map(category => ({
        type: FETCH_TIME_RANGE_FAILURE,
        payload: { category, err },
      })));
    });

const fetchDateRangeEpic = (action$, store, { ajax }) =>
  action$.ofType(FETCH_TIME_RANGE)
    .mergeMap(({ payload }) => {
      const { category, from, to, enclave, refresh } = payload;
      let pageSize;
      if (fromJS(trendCategories).toSet().has(category)) pageSize = 5;
      return ajax.get(createEndpoint(category, { from, to, enclave, pageSize, refresh }))
        .mergeMap(({ response }) => {
          const data = fromJSON(response);
          const actions = [{
            type: FETCH_TIME_RANGE_SUCCESS,
            payload: { category, data }
          }];

          if (category === 'enclaveCorrelations') {
            actions.push({
              type: SET_DEFAULT_ENCLAVE,
              payload: data.get('enclave'),
            });
          }

          if (category === 'enclaveCorrelations' && data.get('status') === 'NO_REPORTS') {
            actions.push(setViewOptions({ category: 'communityTrendingIocs', panel: 'trends' }));
          }

          return Observable.from(actions);
        })
        .catch(err => {
          const { status } = err;
          const action = [{
            type: FETCH_TIME_RANGE_FAILURE,
            payload: { category, err },
          }];
          if (status === 403) {
            action.push(error('You do not have access to this enclave'));
          }
          // TODO_LOG
          //if (status !== 401
          //  && status !== 403
          //  && status !== 502
          //  && status !== 504) {
          //  Raven.captureException(
          //    new Error('An Error occurred in insights fetchDateRangeEpic.'),
          //    {extra: { error: err }}
          //  );
          //}
          return Observable.from(action);
        });
    });

const paginateEpic = (action$, store, { ajax }) =>
  action$.ofType(PAGINATE)
    .mergeMap(({ payload: { category, ...parameters }}) => {
      const endpoint = createEndpoint(category, parameters);
      return ajax.get(endpoint)
        .mergeMap(({ response }) => {
          const result = fromJSON(response);
          return Observable.of({
            type: PAGINATE_SUCCESS,
            payload: {
              category,
              meta: result.delete('data'),
              data: result.get('data'),
            }
          });
        })
        .catch(err => Observable.from([
          { type: PAGINATE_FAILURE, payload: { category, err }},
          error('There was an error fetching additional results'),
        ]));
    });

/* Determine which enclave the backend has selected and save it */
const setEnclaveEpic = action$ =>
  Observable.zip(
    action$.ofType(SET_DEFAULT_ENCLAVE).map(prop('payload')),
    action$.ofType(FETCH_PROFILE_SUCCESS).map(prop('payload')),
    action$.ofType(FETCH_ENCLAVES_SUCCESS).map(prop('payload'))
  )
  .mergeMap(([id, profile, enclaves]) => {
    /* if user-preferences has a default enclave, select it here.
     * otherwise fall back to default enclave selection (alphabetical) */

    const defaultSelectedEnclaveId = profile.preferences
      ? profile.preferences.defaultEnclaveGuid
      : null;

    const defaultId = defaultSelectedEnclaveId
      ? defaultSelectedEnclaveId
      : id;

    let enclave = enclaves.find(e => e.id === defaultId);
    if (!enclave || enclave.get('label') !== 'INTERNAL') {
      enclave = null;
    }

    return Observable.of({
      type: SAVE_DEFAULT_ENCLAVE,
      payload: {
        id: enclave ? enclave.id : null,
        name: enclave ? enclave.name : null,
      }
    });
  });

/* Record user events for dashboard insights & trends clicks */
const analyticsEpic = (action$, store, { ajax }) =>
  action$.ofType(REGISTER_ACTIVITY)
    .mergeMap(({ payload: { id, panel } }) => {
      const analyticsEndpoint = `/services/register-activity/`;
      return ajax
        .post(analyticsEndpoint + `?panel=${panel}&id=${id}`)
        .ignoreElements()
        .catch(err => {
          const { status } = err;
          // TODO_LOG if (status !== 401 && status !== 403 && status !== 502) {
          // TODO_LOG   Raven.captureException(
          // TODO_LOG     new Error('An error occurred hitting the register activity endpoint for the insights analyticsEpic.'),
          // TODO_LOG     {extra: { error: err }}
          // TODO_LOG   );
          // TODO_LOG }
          return Observable.empty();
        });
    });

export default combineEpics(
  initEpic,
  changeEnclaveEpic,
  applyOptionsEpic,
  fetchDateRangeEpic,
  paginateEpic,
  setEnclaveEpic,
  analyticsEpic,
);



// WEBPACK FOOTER //
// ./src/app/modules/insights/epics.js